import { MapPin, Clock, ShieldCheck, ExternalLink } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import ContactSection from '../components/ContactSection';
import { SITE_INFO } from '../data';

function InstagramIcon({ className = "w-5 h-5", ...props }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  );
}

export default function ContactPage() {
  return (
    <>
      <SEOHead 
        title={`Kontakt Os & Indhent Tilbud - ${SITE_INFO.name}`}
        description={`Kontakt ${SITE_INFO.name} på tlf. ${SITE_INFO.phone} eller email ${SITE_INFO.email} for et uforpligtende tilbud på din tømrer- eller snedkeropgave.`}
      />

      {/* Hero Header */}
      <section className="bg-stone-900 text-white py-16 sm:py-20 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="max-w-3xl space-y-4">
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-white tracking-tight">
              Kontakt Virksomheden
            </h1>
            <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
              Vi står altid klar til at rådgive om dit næste byggeprojekt, foretage en besigtigelse eller give dig et gennemskueligt tilbud.
            </p>
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <ContactSection 
        title="Send en forespørgsel direkte til tømrermesteren"
        subtitle="Udfyld formularen herunder med information om dit projekt, så vender vi tilbage med et skriftligt tilbud eller en opringning."
      />

      {/* Location / Working Area Info */}
      <section className="py-16 bg-stone-100 text-stone-900 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 border border-stone-200 shadow-sm space-y-2">
              <MapPin className="w-7 h-7 text-amber-700 mb-2" aria-hidden="true" />
              <h3 className="font-heading font-bold text-lg text-stone-900">Dækningsområde</h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Vi udfører tømrer-, snedker- og murerarbejde i Låsby, Silkeborg, Aarhus, Skanderborg, Ry, Horsens og omegn.
              </p>
            </div>

            <div className="bg-white p-6 border border-stone-200 shadow-sm space-y-2">
              <Clock className="w-7 h-7 text-amber-700 mb-2" aria-hidden="true" />
              <h3 className="font-heading font-bold text-lg text-stone-900">Åbningstider</h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Mandag – Fredag: 07:00 – 16:00<br />
                Lørdag – Søndag: Efter skriftlig aftale
              </p>
            </div>

            <div className="bg-white p-6 border border-stone-200 shadow-sm space-y-2">
              <ShieldCheck className="w-7 h-7 text-amber-700 mb-2" aria-hidden="true" />
              <h3 className="font-heading font-bold text-lg text-stone-900">Firmaoplysninger</h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                {SITE_INFO.name}<br />
                {SITE_INFO.address}<br />
                CVR: {SITE_INFO.cvr}
              </p>
            </div>
          </div>

          {/* Instagram highlight card on Contact page */}
          <div className="mt-8 bg-stone-900 text-white p-6 sm:p-8 border border-stone-800 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-start sm:items-center gap-4">
              <div className="w-12 h-12 bg-amber-600/20 border border-amber-600/40 rounded-lg flex items-center justify-center shrink-0 text-amber-500">
                <InstagramIcon className="w-6 h-6" aria-hidden="true" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg text-white">Følg os på Instagram</h3>
                <p className="text-stone-400 text-sm">
                  Se løbende opdateringer, nye byggeprojekter og håndværk bag kulisserne på <span className="text-amber-400 font-semibold">{SITE_INFO.instagramHandle}</span>
                </p>
              </div>
            </div>
            <a
              href={SITE_INFO.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white font-heading font-bold text-sm px-6 py-3 transition-colors shrink-0 shadow-md"
            >
              <span>Besøg vores Instagram</span>
              <ExternalLink className="w-4 h-4" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
